<!--
  HOME — le corse di OGGI.

  È la pagina su cui si atterra e a cui riporta il logo, ed è anche dove si
  torna dopo aver confermato una prenotazione. Risponde a una domanda sola,
  «cosa succede oggi», e da qui si agisce: si apre una prenotazione, si sposta
  un orario, si aggiungono lavori. Il mese intero è l'Agenda.

  Le righe e la grafica sono quelle dell'Agenda (`AgendaRighe`).

  ⚠️ Si caricano IERI e OGGI e si tengono le sole tratte di oggi. Il server
  filtra sulla data della PRENOTAZIONE, e un ritorno che cade oggi può
  appartenere a una prenotazione di ieri.
-->
<template>
  <div
    v-loading="loading"
    class="oggi"
  >
    <div class="oggi__head">
      <div>
        <h1 class="oggi__title">
          Oggi
        </h1>
        <p class="oggi__sub">
          {{ oggiEsteso }}
        </p>
      </div>
      <div class="oggi__spacer" />
      <button
        type="button"
        class="oggi__btn"
        :disabled="loading"
        @click="carica()"
      >
        Aggiorna
      </button>
      <router-link
        v-if="puoCreare"
        to="/prenotazione/nuova"
        class="oggi__btn oggi__btn--primary"
      >
        Nuova prenotazione
      </router-link>
    </div>

    <div class="oggi__filters">
      <button
        v-for="f in filtriStato"
        :key="f.value"
        type="button"
        class="oggi__pill"
        :class="{ 'is-on': stato === f.value }"
        @click="stato = f.value"
      >
        {{ f.label }} <span class="oggi__count">{{ f.n }}</span>
      </button>

      <div class="oggi__spacer" />

      <!-- La sede si sceglie solo se oggi ce n'è più di una. -->
      <select
        v-if="sedi.length > 1"
        v-model="sede"
        class="oggi__select"
        aria-label="Sede"
      >
        <option value="">
          Tutte le sedi
        </option>
        <option
          v-for="s in sedi"
          :key="s.id"
          :value="s.id"
        >
          {{ s.label }}
        </option>
      </select>
      <div class="oggi__search">
        <input
          v-model="cerca"
          type="text"
          placeholder="Targa, cliente o n° booking"
          aria-label="Cerca fra le corse di oggi"
        >
      </div>
    </div>

    <div
      v-if="troncato"
      class="oggi__warn"
    >
      Il server ha restituito solo una parte delle corse di oggi: l'elenco è incompleto.
    </div>

    <!-- Gli spostamenti interni vanno da una sede all'altra, non appartengono
         a una sede sola: con il filtro escono, e va detto. -->
    <div
      v-if="sede && ciSonoSpostamenti"
      class="oggi__note"
    >
      Gli <strong>spostamenti interni</strong> non appartengono a una sede: con una sede
      scelta restano fuori dall'elenco.
    </div>

    <AgendaRighe
      :righe="righeFiltrate"
      :loading="loading"
      :apribile="apribile"
      :vuoto="vuoto"
      @apri="apri"
    >
      <template #azioni="{ riga }">
        <template v-if="riga.kind === 'booking'">
          <router-link
            v-if="spostabile(riga)"
            :to="`/update-date/${riga.id}`"
            class="oggi__act"
          >
            Modifica data/ora
          </router-link>
          <router-link
            v-if="riga.service !== 'delivery'"
            :to="`/tasks/${riga.id}`"
            class="oggi__act"
          >
            Aggiungi lavori
          </router-link>
        </template>
      </template>
    </AgendaRighe>
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { agendaInWindow } from '@/services/agendaAPI';
import { buildRows, rottaDettaglio } from '@/services/agendaRows';
import { isLoggedStore } from '@/store/logged';
import AgendaRighe from '@/Components/AgendaRighe.vue';

const pad = (n) => String(n).padStart(2, '0');
// In ora LOCALE: `toISOString()` convertirebbe in UTC, e a mezzanotte e mezza
// a Roma darebbe il giorno prima.
const isoLocale = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Due ore: più vicino di così un orario non si sposta, perché il driver
// potrebbe essere già partito.
const ANTICIPO_MINIMO_MS = 2 * 60 * 60 * 1000;

export default {
  name: 'OggiPage',
  components: { AgendaRighe },

  setup() {
    const router = useRouter();
    const store = isLoggedStore();

    const righe = ref([]);
    const loading = ref(false);
    const troncato = ref(false);
    const stato = ref('tutte');
    const sede = ref('');
    const cerca = ref('');

    const oggiIso = ref(isoLocale(new Date()));
    const oggiEsteso = computed(() => {
      const [a, m, g] = oggiIso.value.split('-').map(Number);
      const s = new Date(a, m - 1, g).toLocaleDateString('it-IT', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      });
      return s.charAt(0).toUpperCase() + s.slice(1);
    });

    const puoCreare = computed(() => Boolean(store.bookingType || store.journeyType));

    const giornoDiRiga = (r) => {
      const v = r.start || r.date;
      const d = v ? new Date(v) : null;
      return d && !Number.isNaN(d.getTime()) ? isoLocale(d) : '';
    };

    // `silenzioso`: l'aggiornamento automatico, senza caricamento a schermo
    // e senza messaggi d'errore (riprova al giro dopo).
    const carica = async ({ silenzioso = false } = {}) => {
      if (!silenzioso) loading.value = true;
      // Se la pagina resta aperta oltre la mezzanotte, «Aggiorna» passa al giorno nuovo.
      oggiIso.value = isoLocale(new Date());
      const ieri = new Date();
      ieri.setDate(ieri.getDate() - 1);
      try {
        const dati = await agendaInWindow(isoLocale(ieri), oggiIso.value);
        righe.value = buildRows(dati).filter((r) => giornoDiRiga(r) === oggiIso.value);
        troncato.value = dati.truncated;
      } catch (e) {
        console.error('Corse di oggi non caricate:', e);
        if (silenzioso) return;
        // L'errore parlante lo produce già `handleHttpError` nell'interceptor.
        righe.value = [];
        troncato.value = false;
        ElMessage({ message: 'Corse di oggi non caricate', type: 'error' });
      } finally {
        if (!silenzioso) loading.value = false;
      }
    };
    // L'avanzamento delle corse lo muove il driver dall'app: la pagina si
    // aggiorna da sola ogni minuto, finché è aperta e in vista.
    const AGGIORNA_OGNI_MS = 60 * 1000;
    let timer = null;
    onMounted(() => {
      carica();
      timer = setInterval(() => {
        if (!document.hidden && !loading.value) carica({ silenzioso: true });
      }, AGGIORNA_OGNI_MS);
    });
    onBeforeUnmount(() => clearInterval(timer));

    // ---- filtri ---------------------------------------------------------
    const sedi = computed(() => {
      const per = new Map();
      righe.value.forEach((r) => {
        if (r.headquarterId && !per.has(r.headquarterId)) per.set(r.headquarterId, r.sede);
      });
      return [...per].map(([id, label]) => ({ id, label }));
    });
    const ciSonoSpostamenti = computed(() => righe.value.some((r) => r.kind === 'journey'));

    const inSede = (r) => !sede.value || r.headquarterId === sede.value;
    const conclusa = (r) => r.stateKind === 'muted' || r.stateKind === 'danger';

    const filtriStato = computed(() => {
      const base = righe.value.filter(inSede);
      return [
        { value: 'tutte', label: 'Tutte', n: base.length },
        { value: 'da-svolgere', label: 'Da svolgere', n: base.filter((r) => !conclusa(r)).length },
        { value: 'concluse', label: 'Concluse', n: base.filter(conclusa).length },
      ];
    });

    const righeFiltrate = computed(() => {
      const q = cerca.value.trim().toLowerCase();
      return righe.value.filter((r) => {
        if (!inSede(r)) return false;
        if (stato.value === 'da-svolgere' && conclusa(r)) return false;
        if (stato.value === 'concluse' && !conclusa(r)) return false;
        if (!q) return true;
        return `${r.plate} ${r.customer} ${r.shortId} ${r.carLabel}`.toLowerCase().includes(q);
      });
    });

    const vuoto = computed(() =>
      righe.value.length ? 'Nessuna corsa corrisponde ai filtri.' : 'Nessuna corsa in programma per oggi.'
    );

    // ---- azioni ---------------------------------------------------------
    const apribile = (r) => Boolean(rottaDettaglio(r));
    const apri = (r) => {
      const rotta = rottaDettaglio(r);
      if (rotta) router.push(rotta);
    };

    const spostabile = (r) => {
      if (conclusa(r) || !r.start) return false;
      return new Date(r.start).getTime() - Date.now() > ANTICIPO_MINIMO_MS;
    };

    return {
      righe, righeFiltrate, loading, troncato, stato, sede, cerca, sedi,
      oggiEsteso, puoCreare, filtriStato, ciSonoSpostamenti, vuoto,
      carica, apribile, apri, spostabile,
    };
  },
};
</script>

<style scoped>
.oggi { padding: 4px 8px 32px; text-align: left; }

.oggi__head { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.oggi__title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.015em; }
.oggi__sub { margin: 2px 0 0; font-size: 13px; color: var(--color-text-secondary); }
.oggi__spacer { flex-grow: 1; }

.oggi__btn { display: inline-flex; align-items: center; font: inherit; font-size: 13px; font-weight: 600; color: var(--color-text-primary); background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 8px 14px; cursor: pointer; text-decoration: none; }
.oggi__btn:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
.oggi__btn:disabled { opacity: 0.6; cursor: default; }
.oggi__btn--primary { color: #fff; background: var(--color-primary); border-color: var(--color-primary); }
.oggi__btn--primary:hover:not(:disabled) { color: #fff; background: var(--color-primary-light); }
.oggi__btn:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }

.oggi__filters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
.oggi__pill { display: inline-flex; align-items: center; gap: 7px; background: #fff; border: 1px solid var(--color-border); border-radius: 20px; padding: 6px 14px; font: inherit; font-size: 12.5px; cursor: pointer; }
.oggi__pill.is-on { background: var(--color-text-primary); border-color: var(--color-text-primary); color: #fff; font-weight: 600; }
.oggi__count { color: var(--color-text-secondary); font-variant-numeric: tabular-nums; }
.oggi__pill.is-on .oggi__count { color: rgba(255, 255, 255, 0.65); }

.oggi__select,
.oggi__search input { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; padding: 8px 12px; font: inherit; font-size: 13px; }
.oggi__search input { min-width: 240px; }

.oggi__warn { background: var(--state-attn-bg); border: 1px solid var(--state-attn-line); color: var(--state-attn); border-radius: 6px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; }
.oggi__note { background: var(--color-bg-secondary); border: 1px solid var(--color-border); border-radius: 6px; padding: 10px 14px; font-size: 13px; color: var(--color-text-secondary); margin-bottom: 14px; }

.oggi__act { font-size: 12.5px; font-weight: 600; color: var(--color-primary); text-decoration: none; }
.oggi__act:hover { text-decoration: underline; }
</style>
